import type { Graphics } from 'pixi.js';
import { lerpColor } from '../../color';
import type { Body, WorldDress, WorldFx } from '../../world';

const TAU = Math.PI * 2;
const RUNES = 6;
const FAN = [-0.7, 0, 0.7];

function drop(g: Graphics, x: number, y: number, s: number, angle: number): void {
  g.moveTo(x + Math.cos(angle) * s * 2, y + Math.sin(angle) * s * 2);
  g.arc(x, y, s, angle + Math.PI / 3, angle + (5 * Math.PI) / 3);
  g.closePath();
}

function wobble(g: Graphics, body: Body, r: number, amp: number, lobes: number, phase: number): void {
  const n = 44;
  for (let i = 0; i <= n; i++) {
    const a = (i / n) * TAU;
    const k = r * (1 + amp * Math.sin(lobes * a + phase));
    const px = body.x + Math.cos(a) * k * body.scaleX;
    const py = body.y + Math.sin(a) * k * body.scaleY;
    if (i === 0) g.moveTo(px, py);
    else g.lineTo(px, py);
  }
  g.closePath();
}

export const dress: WorldDress = {
  incomingCircle(k, body, color, alpha) {
    const { g, pal } = k;
    const r = body.radius;
    g.ellipse(body.x + 0.22 * r * body.scaleX, body.y + 0.26 * r * body.scaleY, 0.42 * r * body.scaleX, 0.36 * r * body.scaleY);
    g.fill({ color: lerpColor(color, pal.rim, 0.55), alpha: 0.3 * alpha });
    k.trace(body, 0, 0, 0.8);
    g.stroke({ width: Math.max(1, r * 0.05), color: lerpColor(color, pal.ink, 0.35), alpha: 0.2 * alpha });
    k.highlight(body, 0.6 * alpha);
    k.rimLight(body, 0.4 * alpha);
  },

  mineCircle(k, body, alpha, width, fill, seed) {
    const { g, pal } = k;
    const r = body.radius;
    const t = k.reduced ? 0 : k.time;
    k.trace(body);
    g.fill({ color: fill, alpha });
    g.ellipse(body.x, body.y, 0.55 * r * body.scaleX, 0.55 * r * body.scaleY);
    g.fill({ color: pal.heldLight, alpha: 0.5 * alpha });
    wobble(g, body, 0.74 * r, 0.035, 5, t * 0.8 + seed);
    g.stroke({ width: Math.max(1, width * 0.45), color: pal.heldShade, alpha: 0.2 * alpha });
    wobble(g, body, 0.44 * r, 0.05, 4, -t + seed * 1.7);
    g.stroke({ width: Math.max(1, width * 0.35), color: pal.heldShade, alpha: 0.18 * alpha });
    k.highlight(body, 0.25 * alpha);
    k.trace(body);
    g.stroke({ width: width * 0.8, color: lerpColor(fill, pal.rim, 0.15), alpha, join: 'round' });
  },

  target(k, body, alpha, _spread, reach, spin, edge) {
    const { g, pal } = k;
    const r = body.radius;
    k.trace(body);
    g.fill({ color: lerpColor(pal.bg, 0x000000, 0.35), alpha: 0.5 * alpha });
    k.trace(body);
    g.fill({ color: pal.spot, alpha: 0.07 * alpha });
    for (let j = 0; j < 3; j++) {
      const rr = r + (reach - r) * (0.3 + 0.35 * j);
      if (body.shape === 'circle') wobble(g, body, rr, 0.02, 6, j * 1.3);
      else k.trace(body, 0, 0, rr / Math.max(1, r));
      g.stroke({ width: Math.max(1, edge * (0.55 - 0.12 * j)), color: pal.rim, alpha: (0.36 - 0.1 * j) * alpha, join: 'round' });
    }
    const s = Math.max(2.2, k.unit * 0.011);
    k.ringDots(body, reach * 1.06, RUNES, spin, (x, y) => {
      g.circle(x, y, s);
    });
    g.fill({ color: pal.spot, alpha: 0.75 * alpha });
    k.ringDots(body, reach * 1.06, RUNES, spin, (x, y, _i, a) => {
      for (const f of FAN) {
        g.moveTo(x + Math.cos(a + f) * s * 1.7, y + Math.sin(a + f) * s * 1.7);
        g.lineTo(x + Math.cos(a + f) * s * 3, y + Math.sin(a + f) * s * 3);
      }
    });
    g.stroke({ width: Math.max(1, s * 0.5), color: pal.spot, alpha: 0.6 * alpha, cap: 'round' });
    k.trace(body);
    g.stroke({ width: edge, color: pal.rim, alpha: 0.85 * alpha, join: 'round' });
  },

  pip(k, x, y, radius, on, last, alpha, width) {
    const { g, pal } = k;
    const s = radius * 0.95;
    if (on) g.circle(x, y, radius * 2).fill({ color: pal.incoming.circle, alpha: 0.18 * alpha });
    drop(g, x, y + s * 0.5, s, -Math.PI / 2);
    if (on) g.fill({ color: last ? pal.spot : lerpColor(pal.incoming.circle, pal.rim, 0.3), alpha });
    else g.fill({ color: pal.path, alpha: 0.22 * alpha });
    g.stroke({ width: width * 0.8, color: pal.rim, alpha: (on ? 0.9 : 0.45) * alpha, join: 'round' });
  },

  path(k, points, count, alpha) {
    const { g, pal } = k;
    const amp = k.unit * 0.006;
    const lambda = k.unit * 0.07;
    const phase = 0;
    const step = Math.max(3, k.unit * 0.012);
    const strands: readonly (readonly [number, number, number, number])[] = [
      [1, 0, 2.4, 1.3],
      [0.6, 2.1, 1.2, 1],
    ];
    for (const [scale, offset, weight, width] of strands) {
      let dist = 0;
      let started = false;
      for (let i = 2; i < count; i += 2) {
        const x0 = points[i - 2]!;
        const y0 = points[i - 1]!;
        const x1 = points[i]!;
        const y1 = points[i + 1]!;
        const len = Math.hypot(x1 - x0, y1 - y0);
        if (len === 0) continue;
        const nx = -(y1 - y0) / len;
        const ny = (x1 - x0) / len;
        const n = Math.min(120, Math.max(2, Math.ceil(len / step)));
        for (let s = 0; s <= n; s++) {
          const t = s / n;
          const o = Math.sin(((dist + len * t) / lambda) * TAU + phase + offset) * amp * scale;
          const px = x0 + (x1 - x0) * t + nx * o;
          const py = y0 + (y1 - y0) * t + ny * o;
          if (!started) {
            g.moveTo(px, py);
            started = true;
          } else g.lineTo(px, py);
        }
        dist += len;
      }
      if (started) g.stroke({ width, color: pal.path, alpha: Math.min(1, alpha * weight), cap: 'round', join: 'round' });
    }
  },
};

export const fx: WorldFx = {
  bit(g, x, y, size, _i, color, alpha, rot) {
    drop(g, x, y, size * 0.8, -Math.PI / 2 + 0.5 * Math.sin(rot));
    g.fill({ color, alpha });
  },

  milestone(g, m, pal) {
    const r = m.r * 1.05;
    g.circle(m.x, m.y, r).stroke({ width: m.width * 3, color: pal.held, alpha: m.fade * 0.1 });
    for (let j = 0; j < 2; j++) {
      g.circle(m.x, m.y, m.r * (1 + 0.2 * j)).stroke({
        width: Math.max(1, m.width * (0.8 - 0.25 * j)),
        color: pal.rim,
        alpha: m.fade * (0.45 - 0.17 * j),
      });
    }
    const fall = m.grow * m.unit * 0.12;
    const s = Math.max(2, m.unit * 0.012);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * TAU + 0.3;
      const px = m.x + Math.cos(a) * m.r * 1.25;
      const py = m.y + Math.sin(a) * m.r * 1.25 + fall;
      if (i % 2 === 1) drop(g, px, py, s, -Math.PI / 2);
      else g.star(px, py, 4, s * 1.4, s * 0.4, 0);
      g.fill({ color: i % 2 === 1 ? pal.heldLight : pal.spot, alpha: m.fade * 0.6 });
    }
  },

  trail(g, x, y, size, angle) {
    drop(g, x, y, size * 0.7, angle);
  },
};
